import { ChevronDownIcon } from "@heroicons/react/24/outline";
import { SignalIcon } from "@heroicons/react/24/solid";
import clsx from "clsx";
import React from "react";

import { Tooltip } from "@fronterahealth/frontera-ui-components";

import { MediaTimelineCategory } from "@pages/VideoSessionsV2/components/MediaTimeline/MediaTimeline";
import { getColorForGoal } from "@pages/VideoSessionsV2/components/MediaTimeline/helper";
import { useVideoSessionData } from "@providers/VideoSessionProviderV2";

const MomentTitleRow = ({
  id,
  title,
  isCollapsible,
  isCollapsed,
  color,
  onToggleExpand,
}: {
  id: string;
  title: string;
  isCollapsible: boolean;
  isCollapsed: boolean;
  color: string;
  onToggleExpand: (id: string) => void;
}) => {
  return (
    <div className="h-10 flex items-center border-b text-sm text-limestone-600">
      <span
        className="min-w-1 h-9"
        style={{
          backgroundColor: color,
        }}
      />
      <div className="flex items-center gap-2 p-2">
        <div
          className={clsx(
            "rounded-sm p-1 bg-white flex items-center justify-center",
            isCollapsible ? "cursor-pointer" : "invisible",
          )}
        >
          <ChevronDownIcon
            className={clsx("w-4 h-4", isCollapsed && "-rotate-90")}
            strokeWidth={5}
            onClick={() => onToggleExpand(id)}
          />
        </div>
        <div className="w-[80%]">
          <Tooltip text={title} showInfoIcon={false}>
            <div className="truncate w-36">{title}</div>
          </Tooltip>
        </div>
      </div>
    </div>
  );
};
const MomentSignalTitleRow = ({ title, color }: { title: string; color: string }) => {
  return (
    <div className="h-10 flex items-center border-b text-sm text-limestone-600">
      <span
        className="min-w-1 h-9"
        style={{
          backgroundColor: color,
        }}
      />
      <div className="flex items-center gap-2 p-2">
        <div className="rounded-sm p-1 bg-white flex items-center justify-center">
          <SignalIcon className="w-4 h-4 text-limestone-600!" />
        </div>
        <div className="w-[80%]">
          <Tooltip text={title} showInfoIcon={false}>
            <div className="truncate w-36">{title}</div>
          </Tooltip>
        </div>
      </div>
    </div>
  );
};
export const MomentTitleRows = ({
  data,
  expandedRowIds,
  onToggleExpand,
  unpinnedSectionCollapsed,
  onToggleUnpinnedSection,
}: {
  data: MediaTimelineCategory[];
  expandedRowIds: string[];
  onToggleExpand: (id: string) => void;
  unpinnedSectionCollapsed: boolean;
  onToggleUnpinnedSection: () => void;
}) => {
  const { mediaClassificationsData } = useVideoSessionData();
  const pinnedData = data.filter((cat) => cat.isPinned);
  const unpinnedData = data.filter((cat) => !cat.isPinned);
  const hasBothSections = pinnedData.length > 0 && unpinnedData.length > 0;

  const renderCategory = (cat: MediaTimelineCategory) => {
    const isExpanded = expandedRowIds.includes(cat.id);
    const isCollapsible = cat.children?.length > 0;
    const color = getColorForGoal(cat.title);
    return (
      <React.Fragment key={cat.id}>
        <MomentTitleRow
          id={cat.id}
          title={cat.title}
          isCollapsible={isCollapsible}
          isCollapsed={!isExpanded}
          color={color}
          onToggleExpand={onToggleExpand}
        />
        {isExpanded &&
          cat.children?.map((child) => (
            <MomentTitleRow
              key={child.id}
              id={child.id}
              title={child.title}
              isCollapsible={false}
              isCollapsed={false}
              color={color}
              onToggleExpand={onToggleExpand}
            />
          ))}
      </React.Fragment>
    );
  };
  const cryingAndScreamingSignal = {
    id: "crying-and-screaming",
    title: "Crying and Screaming",
    color: getColorForGoal("Crying and Screaming"),
  };
  const signalData = [cryingAndScreamingSignal];

  const renderSignalCategory = (signal: (typeof signalData)[number]) => {
    return <MomentSignalTitleRow key={signal.id} title={signal.title} color={signal.color} />;
  };

  return (
    <>
      {/* Render pinned goal titles */}
      {pinnedData.map(renderCategory)}
      {/* Render divider if there are both sections */}
      {hasBothSections && (
        <div
          className="h-[10px] flex items-center bg-limestone-200 cursor-pointer border-b"
          onClick={onToggleUnpinnedSection}
        >
          <ChevronDownIcon
            className={clsx("ml-2 w-4 h-4 transition-transform", unpinnedSectionCollapsed && "-rotate-90")}
            strokeWidth={2}
          />
        </div>
      )}
      {/* Render unpinned goal titles if not collapsed */}
      {!unpinnedSectionCollapsed && (
        <>
          {/* Render signal titles */}
          {mediaClassificationsData.length > 0 && signalData.map(renderSignalCategory)}
          {unpinnedData.map(renderCategory)}
        </>
      )}
    </>
  );
};
